import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import CryptoJS from "crypto-js";
import { pick } from "lodash";

import { store } from "../redux";
import { FlattenedObjectType, NestedObjectType } from "../types";
import { convertSnakeToTitleCase } from "./commonUtils";

export const BASE_API_URL = "/api/v1";

// API AUTH TOKEN
const API_REQUEST_HEADER_OPTIONS = {
	// "X-Cue": "34db55e07f7b39df480284397f7f42ec",
	// "X-Salt": "683239",
	// "X-Alpha": "21",
	Accept: "application/json"
};

export const decryptToken = (encryptedToken: string) => {
	const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY ? String(process.env.REACT_APP_ENCRYPTION_KEY) : "";
	return CryptoJS.AES.decrypt(encryptedToken, encryptionKey).toString(CryptoJS.enc.Utf8);
};

function getAuthToken() {
	const token = decryptToken(store.getState().auth.user.token);
	return token ? `Token ${token}` : "";
}

export const getAPIHeaderOptions = (isAuthRequired = false) => {
	const token = getAuthToken();

	if (isAuthRequired && token) {
		return {
			...API_REQUEST_HEADER_OPTIONS,
			Authorization: token
		};
	}

	return API_REQUEST_HEADER_OPTIONS;
};

function flattenObject(object: NestedObjectType, parentKey?: string): FlattenedObjectType {
	let result: FlattenedObjectType = {};

	for (const [key, value] of Object.entries(object)) {
		if (typeof value === "object" && !Array.isArray(value)) {
			const flattenedValue = flattenObject(value, parentKey ? `${parentKey}.${key}` : key);

			result = {
				...result,
				...flattenedValue
			};
		} else {
			if (parentKey) result[`${parentKey}.${key}`] = value;
			else result[key] = value;
		}
	}

	return result;
}

function extractErrorMessage(error: FlattenedObjectType, parentKey?: string): string {
	if ("detail" in error) error = pick(error, ["detail"]);
	else if ("details" in error) error = pick(error, ["details"]);

	const errorKey = Object.keys(error)[0];
	const errorValue = Object.values(error)[0] ?? "Something went wrong";

	if (typeof errorValue === "object") {
		return extractErrorMessage(errorValue, errorKey);
	}

	const errorMessage = String(errorValue);

	if (errorMessage.toLowerCase().includes("this field")) {
		return errorMessage.replace(/this field/i, convertSnakeToTitleCase(parentKey || errorKey || "This field"));
	}

	if (errorMessage.toLowerCase().includes("this value")) {
		return errorMessage.replace(/this value/i, convertSnakeToTitleCase(parentKey || errorKey || "This value"));
	}

	return errorMessage;
}

export const transformErrorResponse = (response: FetchBaseQueryError): FetchBaseQueryError => {
	if (response.data) {
		const error = flattenObject(response.data);
		response.data = extractErrorMessage(error);
	} else {
		response.data = "Something went wrong";
	}

	return response;
};
